import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  renewResultSchema,
  renewalHistoryListSchema,
  renewalListSchema,
  renewalOpenSchema,
  renewalRenewSchema,
  renewalSchema,
  renewalUpdateSchema,
  upcomingRenewalPageSchema,
  type Renewal,
  type RenewalOpen,
  type RenewalRenew,
  type RenewalUpdate,
} from '@bekebi/contracts/api';
import { apiRequest } from '../../api/client';
import { serviceKeys } from '../services/api';

/**
 * Data access for the Admin Renewals area — the existing renewal endpoints only:
 *   GET  /api/renewals/upcoming[?before=&cursor=&limit=]   open terms by renewal date (renewals.view)
 *   GET  /api/renewals/:id                                  one term (renewals.view)
 *   GET  /api/services/:id/renewals                         every term of a service (renewals.view)
 *   GET  /api/services/:id/renewal-history                  append-only history (renewals.view)
 *   POST /api/services/:id/renewals                         open a term (renewals.manage)
 *   PATCH /api/renewals/:id                                 edit an open term (renewals.manage)
 *   POST /api/renewals/:id/renew                            close it and open the next (renewals.manage)
 * Condition and countdown are derived by the API (Africa/Lagos) and shown as returned.
 */
export const renewalKeys = {
  all: ['renewals'] as const,
  upcoming: (before: string | null) => [...renewalKeys.all, 'upcoming', before] as const,
  detail: (id: string) => [...renewalKeys.all, 'detail', id] as const,
  forSubject: (path: string) => [...renewalKeys.all, 'subject', path] as const,
  history: (path: string) => [...renewalKeys.all, 'history', path] as const,
};

/**
 * Where a term's subject lives in the API: a service, or an infrastructure resource (the VPS, the
 * domain). Every term has exactly one.
 */
export function subjectPath(renewal: Pick<Renewal, 'serviceId' | 'resourceId'>): string {
  return renewal.serviceId !== null
    ? `/services/${renewal.serviceId}`
    : `/infrastructure/resources/${renewal.resourceId}`;
}

export function useUpcomingRenewals(before: string | null) {
  return useInfiniteQuery({
    queryKey: renewalKeys.upcoming(before),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (before) params.set('before', before);
      if (pageParam) params.set('cursor', pageParam);
      return apiRequest(`/renewals/upcoming?${params}`, { schema: upcomingRenewalPageSchema });
    },
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useRenewal(id: string) {
  return useQuery({
    queryKey: renewalKeys.detail(id),
    queryFn: () => apiRequest(`/renewals/${id}`, { schema: renewalSchema }),
  });
}

/** Every term of one subject (a service or a resource), from its subject path. */
export function useSubjectRenewals(path: string) {
  return useQuery({
    queryKey: renewalKeys.forSubject(path),
    queryFn: async () =>
      (await apiRequest(`${path}/renewals`, { schema: renewalListSchema })).items,
  });
}

export function useRenewalHistory(path: string) {
  return useQuery({
    queryKey: renewalKeys.history(path),
    queryFn: async () =>
      (
        await apiRequest(`${path}/renewal-history`, {
          schema: renewalHistoryListSchema,
        })
      ).items,
  });
}

/** Renewal changes also change each service's renewal summary: re-read both. */
async function refresh(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: renewalKeys.all }),
    queryClient.invalidateQueries({ queryKey: serviceKeys.all }),
  ]);
}

export function useOpenRenewal() {
  const queryClient = useQueryClient();
  return useMutation({
    /** `subject`: "service:<id>" or "resource:<id>" — what the new term renews. */
    mutationFn: ({ subject, ...body }: RenewalOpen & { subject: string }) =>
      apiRequest(`${subjectPathOf(subject)}/renewals`, {
        method: 'POST',
        body: renewalOpenSchema.parse(body),
        schema: renewalSchema,
      }),
    onSuccess: async () => refresh(queryClient),
  });
}

export function useUpdateRenewal(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: RenewalUpdate) =>
      apiRequest(`/renewals/${id}`, {
        method: 'PATCH',
        body: renewalUpdateSchema.parse(patch),
        schema: renewalSchema,
      }),
    onSuccess: async () => refresh(queryClient),
  });
}

export function useRenewTerm(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RenewalRenew) =>
      apiRequest(`/renewals/${id}/renew`, {
        method: 'POST',
        body: renewalRenewSchema.parse(body),
        schema: renewResultSchema,
      }),
    // Not awaited: the refreshed data shows this term as closed, which removes the Renew form. The
    // caller's success callback (going to the new term) must run before that happens.
    onSuccess: () => {
      void refresh(queryClient);
    },
  });
}

/** "service:<id>" or "resource:<id>" (the open-term form's choice) as an API path. */
export function subjectPathOf(subject: string): string {
  const [kind, id] = subject.split(':');
  return kind === 'resource' ? `/infrastructure/resources/${id}` : `/services/${id}`;
}
